import { Axios } from 'aio-deps';
import { Alert, type AP_alert, Loading } from '../aio-popup';
import AIODate from '../aio-date';
import { AddQueryParamsToUrl, type I_logItem, IsEqual, IsNetworkDisconnected, Storage, useLogs } from '../aio-utils';

export type AA_api<T> = {
    loader?: string, loading?: boolean, mockDelay?: number, headers?: any, messageTime?: number, showError?: boolean, name: string, url: string,
    method: 'post' | 'get' | 'delete' | 'put' | 'patch', description?: string, required?: boolean, reload?: boolean, logout?: boolean, body?: any,
    loadingParent?: string, cache?: { name: string, expiredAt?: number, updateBySuccess?: boolean, dependency?: any }, mock?: boolean,
    queryObject?: { [key: string]: any }, retryAction?: () => void, logResponse?: (status: AA_status<T>) => any, offlineId?: string, key?: string[], staleTime?: number
}
type AA_status<T> = { success: boolean, response: T, errorMessage?: string, isNetworkError?: boolean, isOffline?: boolean, isWriteCache?: boolean, isReadByCache?: boolean, isReadByKey?: boolean }
type I_cachedApi<T> = { api: AA_api<T>, response: any, dependency: any }
type I_defaultKeys = 'loader' | 'mockDelay' | 'headers' | 'token' | 'loading'
export type AA_log = { apiName: string, apiConfig?: AA_api<any>, status?: AA_status<any>, message?: string }
export type AA_props = {
    id: string, lang?: 'en' | 'fa', base_url?: string, logout?: () => void, token?: string, checkIsOffile?: () => Promise<boolean>, logsHook?: ReturnType<typeof useLogs>,
    handleErrorMessage: (err: any, api: AA_api<any>) => string, mockServer?: any, option?: { [key in keyof AA_api<any>]?: (api: AA_api<any>) => any }
}
export default class AIOApis {
    private props: AA_props;
    private mockServer: any;
    private context: any = {};
    private option?: { [key in keyof AA_api<any>]?: (api: AA_api<any>) => any }
    private cache: Cache;
    private defaults: { [key in I_defaultKeys]?: any } = {}
    private offline: OfflineClass;
    actions: {
        getNow: () => number[],
        setToken: (token: string) => void,
        getToken: () => string | undefined,
        addAlert: (p: { type: 'success' | 'error' | 'warning' | 'info', title?: string, text: string, time?: number }) => void,
        getCachedResponse: (cacheName: string) => any
        updateCachedResponse: (cacheName: string) => Promise<AA_status<any>>
        removeCachedResponse: (cacheName: string) => void,
        setContextByKeyValue: (key: string, value: any) => void,
        getContextByKeyValue: (key: string) => any,
        dumpCache: () => { __CACHE__: any }
    }
    private DATE: AIODate = new AIODate()
    keyValues: { [key: string]: { response: any, expiredAt: number } } = {}
    private logsHook: ReturnType<typeof useLogs> | undefined
    constructor(props: AA_props) {
        this.defaults = { token: props.token, mockDelay: 3000,loading:true }
        this.mockServer = props.mockServer;
        this.props = props
        this.option = props.option;
        this.offline = new OfflineClass({ id: props.id, request: this.request.bind(this) })
        this.cache = new Cache(this.props.id, this.responseToResult);
        this.logsHook = props.logsHook
        this.actions = {
            getNow: this.getNow.bind(this),
            setToken: this.setToken.bind(this),
            getToken: this.getToken.bind(this),
            addAlert: this.addAlert.bind(this),
            getCachedResponse: this.cache.getCachedReponse,
            updateCachedResponse: this.cache.updateCachedResponse,
            removeCachedResponse: this.cache.removeCachedResponse,
            dumpCache: this.cache.dump,
            setContextByKeyValue: this.setContextByKeyValue.bind(this),
            getContextByKeyValue: this.getContextByKeyValue.bind(this),
        }
    }
    private setContextByKeyValue = (key: string, value: any) => this.context = { ...this.context, [key]: value };
    private getContextByKeyValue = (key: string) => this.context[key]
    private getNow = (jalali?: boolean): number[] => this.DATE.getToday(jalali)
    private setToken = (token: string) => Axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
    private getToken = () => this.defaults.token
    private addAlert = (p: AP_alert) => Alert({ ...p, className: 'aio-apis-popup', closeText: this.props.lang === 'fa' ? 'بستن' : 'Close' })
    private getUrl = (api: AA_api<any>) => {
        let { url, queryObject } = api
        if (this.props.base_url && url.indexOf(this.props.base_url) !== 0 && url.indexOf('http') !== 0) { url = this.props.base_url + url; }
        if (queryObject) { url = AddQueryParamsToUrl(url, queryObject) }
        return url
    }
    private apisInFlight: Map<string, Promise<AA_status<any>>> = new Map();
    private isOffline = async () => {
        if (!this.props.checkIsOffile) { return false }
        const response = await this.props.checkIsOffile()
        return await IsNetworkDisconnected(response)
    }
    private getMockResponse = async (api: AA_api<any>): Promise<false | { errorMessage: string, response: any, success: boolean, isNetworkError: boolean }> => {
        const { mockDelay, mock } = api
        return new Promise((resolve) => {
            setTimeout(async () => {
                if (mock === undefined || !this.mockServer) { resolve(false) }
                const isNetworkError = await this.isOffline()
                if (mock === true) {
                    if (!this.mockServer[api.name]) { resolve(false) }
                    else {
                        const errorMessage = '';
                        const response = this.mockServer[api.name](api)
                        resolve({ errorMessage, response, success: true, isNetworkError });
                    }
                }
                else if (mock === false) {
                    if (!this.mockServer.error) { return false }
                    const response = this.mockServer.error()
                    const errorMessage = await this.props.handleErrorMessage(response, api)
                    resolve({ errorMessage, response, success: false, isNetworkError });
                }
            }, mockDelay);
        });
    }
    addLog = (log: I_logItem) => {
        if (!this.logsHook) { return }
        this.logsHook.add([{...log,tags:['_apis_']}])
    }
    addApiLog = (api: AA_api<any>, status: AA_status<any>) => {
        const responseCode = api.logResponse ? api.logResponse(status) : ''
        this.addLog({
            id: `api_${api.name}`,
            data: { status: { ...status, response: responseCode }, apiConfig: api },
            status: status.success ? 'success' : 'error',
            message: status.errorMessage || 'success'
        })
    }
    log_return_response_from_cache_because_of_unsuccess_request = (api: AA_api<any>) => {
        const logMessage = `api name ${api.name}:return response from cache because of unsuccess request`;
        const logId = `${api.name}_return_response_from_cache_because_of_unsuccess_request`
        this.addLog({ message: logMessage, id: logId })
    }
    private responseToResult = async <T>(api: AA_api<T>): Promise<AA_status<T>> => {
        const { headers, name, mock, body } = api
        const { handleErrorMessage } = this.props;
        this.setToken(this.defaults.token || ''); // ست کردن توکن روی Axios
        if (!handleErrorMessage) {
            const errorMessage = `missing onCatch in api: ${name}`
            return { errorMessage, success: false, response: undefined as any }
        }
        try {
            const url = this.getUrl(api);
            if (mock !== undefined && this.mockServer) {
                const res = await this.getMockResponse(api)
                if (res !== false) {
                    return { success: res.success, response: res.response, errorMessage: res.errorMessage, isNetworkError: res.isNetworkError }
                }
                return { success: false, response: {} as any, errorMessage: `mockServer.${api.name} is not defined` }
            }
            let response: T = await Axios({ method: api.method, url, data: body, headers: headers === false ? undefined : headers })
            return { success: true, response }
        }
        catch (response: any) {
            try {
                let errorMessage = await handleErrorMessage(response, api)
                let isNetworkError: boolean = await this.isOffline();
                return { errorMessage, success: false, response, isNetworkError }
            }
            catch (err: any) { return { errorMessage: err.message, success: false, response } }
        }
    }

    private loading = (api: AA_api<any>, state: boolean) => {
        const { name, loadingParent, loading, loader } = api;
        if (loading) { new Loading(loader)[state ? 'show' : 'hide'](name, loadingParent) }
    }

    private getApi = (api: AA_api<any>) => {
        const keys: (keyof AA_api<any>)[] = [
            'loader', 'loading', 'mockDelay', 'headers', 'messageTime', 'showError', 'description',
            'required', 'reload', 'logout', 'body', 'loadingParent', 'cache', 'mock', 'queryObject', 'retryAction', 'logResponse', 'offlineId'
        ]
        const res: AA_api<any> = { ...api }
        for (let key of keys) {
            if (res[key] === undefined) {
                if (this.option && this.option[key]) {
                    (res as any)[key] = this.option[key](res)
                }
            }
            if (res[key] === undefined) { (res as any)[key] = (this.defaults as any)[key] }
        }
        return res
    }
    serializeKey(key: any[]): string {
        return key.map(v => `${typeof v}:${String(v)}`).join('|');
    }
    private handleKeyValues = (api: AA_api<any>): false | AA_status<any> => {
        if (api.key && api.key.length) {
            const key = this.serializeKey(api.key);
            const keyValue = this.keyValues[key];
            if (keyValue) {
                if (keyValue.expiredAt > Date.now()) {
                    this.addLog({ message: `api name ${api.name}:return response from keyValues`, id: `${api.name}_return_response_from_keyValues` })
                    return { success: true, response: keyValue.response, isReadByKey: true }
                }
                else {
                    this.addLog({ message: `api name ${api.name}:remove keyValues by expire`, id: `${api.name}_remove_keyValues_by_expire` })
                    delete this.keyValues[key]
                }
            }
        }
        return false
    }
    handleCache = (api: AA_api<any>): false | AA_status<any> => {
        const { cache, name } = api;
        if (cache) {
            if (!cache.updateBySuccess) {
                if (this.cache.isDependencyChanged(api)) {
                    this.addLog({ message: `api name ${name}:remove cache by change dependency`, id: `${name}_remove_cache_by_change_dependency` })
                    this.cache.removeCachedResponse(cache.name)
                }
                else {
                    let cachedResponse = this.cache.getCachedReponse(cache.name);
                    if (cachedResponse !== undefined) {
                        this.addLog({ message: `api name ${name}:return response from cache`, id: `${name}_return_response_from_cache` })
                        return { success: true, response: cachedResponse, isReadByCache: true }
                    }
                }
            }
        }
        return false
    }
    handleOffline = (api: AA_api<any>, status: AA_status<any>): false | AA_status<any> => {
        if (status.isNetworkError && api.offlineId) {
            this.offline.add(api)
            return { success: false, response: null as any, isNetworkError: true, isOffline: true }
        }
        return false
    }
    request = async <T>(Api: AA_api<T>): Promise<AA_status<T>> => {
        const api = this.getApi(Api); // merge گزینه‌ها با defaults و option‌ها
        const { name, cache } = api;
        const key = api.key ? this.serializeKey(api.key) : name; // کلید یکتا برای dedupe: یا key اختصاصی یا نام API
        // 1️⃣ بررسی اینکه آیا request با همین key در حال اجراست
        const inFlight = this.apisInFlight.get(key);
        if (inFlight) return inFlight; // اگر هست، همان Promise را برگردان تا dedupe شود
        // 2️⃣ ایجاد promise اصلی request
        const promise = (async (): Promise<AA_status<T>> => {
            const keyValueRes = this.handleKeyValues(api);// 3️⃣ بررسی keyValues (cache حافظه داخلی)
            if (keyValueRes !== false) return keyValueRes; // اگر جواب معتبر در حافظه بود، برگردان
            const cacheRes = this.handleCache(api);// 4️⃣ بررسی cache پیشرفته (Storage)
            if (cacheRes !== false) return cacheRes; // اگر جواب cache معتبر بود، برگردان
            this.loading(api, true);// 5️⃣ شروع loading UI
            const status = await this.responseToResult<T>(api);// 6️⃣ درخواست اصلی به سرور یا mock
            this.addApiLog(api, status); // ثبت log درخواست
            const { errorMessage, success, response, isNetworkError } = status;
            this.loading(api, false); // پایان loading
            const offlineRes = this.handleOffline(api, status);// 7️⃣ بررسی وضعیت offline و اضافه کردن به لیست آفلاین در صورت نیاز
            if (offlineRes !== false) return offlineRes;
            // 8️⃣ بررسی وضعیت unsuccessful و بازگشت از cache در صورت تنظیم updateBySuccess
            const unSuccessRes = this.handleUnsuccess(api, status);
            if (unSuccessRes !== false) return unSuccessRes;
            // 9️⃣ ثبت موفقیت در keyValues و cache
            this.handleSuccess(api, response);
            //  🔟 بازگشت نتیجه نهایی
            return { response, success, errorMessage, isNetworkError, isWriteCache: !!cache && !!cache.name };
        })();

        // 11️⃣ ثبت promise در inFlight برای dedupe
        this.apisInFlight.set(key, promise);

        // 12️⃣ حذف promise از inFlight پس از اتمام، چه موفق چه خطا
        promise.finally(() => this.apisInFlight.delete(key));

        // 13️⃣ برگرداندن promise
        return promise;
    };
    setReponseByKey = (key: any[], response: any, staleTime?: number) => {
        const serial = this.serializeKey(key);
        this.keyValues = { ...this.keyValues, [serial]: { response, expiredAt: Date.now() + (staleTime || 10000) } };
    }
    private handleSuccess = (api: AA_api<any>, response: any) => {
        const { name, cache, key, staleTime } = api;
        if (key) { this.setReponseByKey(key, response, staleTime); }
        if (cache) {
            const cacheItem: I_cachedApi<any> = { api, response, dependency: cache.dependency }
            this.addLog({ message: `api name ${api.name}:response cached`, id: `${name}_response_cached` })
            this.cache.setCache(cache.name, cacheItem);
        }
    }
    private handleUnsuccess = (api: AA_api<any>, status: AA_status<any>): false | AA_status<any> => {
        if (status.success) { return false }
        if (api.cache && api.cache.updateBySuccess) {
            let cachedResponse = this.cache.getCachedReponse(api.cache.name);
            if (cachedResponse !== undefined) {
                this.log_return_response_from_cache_because_of_unsuccess_request(api)
                return { success: true, response: cachedResponse, isReadByCache: true }
            }
        }
        this.handleErrorMessage(api, status);
        return false
    }
    private handleErrorMessage = (api: AA_api<any>, status: AA_status<any>) => {
        if (api.showError === false || typeof status.errorMessage !== 'string') { return }
        this.addAlert(getErrorAlertConfig(api, status, this.props.logout || (() => { }), this.props.lang === 'fa'));
    }
}

// ---------------- Cache پیشرفته ----------------
export type I_mock = { url: string, delay: number, method: 'post' | 'get' | 'delete' | 'put' | 'patch', result: ((body: any) => { status: number, data: any }) }
type I_callCachedApi = (api: AA_api<any>) => Promise<AA_status<any>>;


class Cache {
    // مرجع به Storage برای ذخیره‌سازی cache
    private cacheStorage: Storage;

    // متد callback برای فراخوانی API در صورت نیاز به refresh
    private responseToResult: (api: AA_api<any>) => Promise<AA_status<any>>;

    // نگه داشتن promiseهای در حال اجرا برای جلوگیری از request همزمان تکراری
    private inflight = new Map<string, Promise<AA_status<any>>>();
    constructor(id: string, responseToResult: (api: AA_api<any>) => Promise<AA_status<any>>) {
        this.cacheStorage = new Storage(id);
        this.responseToResult = responseToResult;
        // بررسی اینکه آیا کد در محیط مرورگر اجرا می‌شود
        const isClient = typeof window !== 'undefined' && typeof window.addEventListener === 'function';
        // اگر در مرورگر هستیم، event listener برای refresh خودکار cache
        if (isClient) {
            window.addEventListener('focus', this.refreshStaleCache); // وقتی کاربر به صفحه برگشت
            window.addEventListener('online', this.refreshStaleCache); // وقتی اتصال اینترنت برگشت
        }
    }
    // بررسی اینکه آیا آیتم cache منقضی شده است یا نه
    private isStale(item: I_cachedApi<any>): boolean {
        const expiredIn = item.api.cache?.expiredAt;
        return !expiredIn ? false : Date.now() > expiredIn;
    }
    // فراخوانی dedup شده برای جلوگیری از درخواست همزمان مشابه
    private callApi: I_callCachedApi = async (api) => await this.responseToResult(api);
    private dedupedCall(cacheName: string, api: AA_api<any>): Promise<AA_status<any>> {
        // اگر درخواست مشابه در حال اجراست، همان promise را برگردان
        if (this.inflight.has(cacheName)) { return this.inflight.get(cacheName)!; }
        // ایجاد promise جدید برای درخواست API
        const promise = this.callApi(api).finally(() => this.inflight.delete(cacheName));
        // ثبت promise در inflight
        this.inflight.set(cacheName, promise);
        return promise;
    }
    // بروزرسانی خودکار cacheهای منقضی شده
    private refreshStaleCache = async () => {
        const keys = this.cacheStorage.getKeys(); // دریافت تمام کلیدهای ذخیره شده
        for (const key of keys) {
            const cachedApi: I_cachedApi<any> | undefined = this.cacheStorage.load(key); // بارگذاری cache
            if (!cachedApi || this.isStale(cachedApi)) { continue }
            this.dedupedCall(key, cachedApi.api).then(status => {
                if (status.success) { this.setCache(key, { ...cachedApi, response: status.response }); }
            });
        }
    };
    // ذخیره یک آیتم cache
    setCache = (cacheName: string, cachedApi: I_cachedApi<any>) => this.cacheStorage.save(cacheName, cachedApi, cachedApi.api.cache?.expiredAt);
    // دریافت پاسخ cache، اگر stale باشد، در پس‌زمینه refresh می‌شود
    getCachedReponse = (cacheName: string): any => {
        const cachedApi = this.cacheStorage.load(cacheName);
        if (!cachedApi) return undefined;
        // اگر stale → background refetch
        if (this.isStale(cachedApi)) {
            this.dedupedCall(cacheName, cachedApi.api).then(status => {
                if (status.success) { this.setCache(cacheName, { ...cachedApi, response: status.response, createdAt: Date.now() }); }
            });
        }
        return cachedApi.response; // برگرداندن پاسخ cache
    };
    // دریافت cache از روی نام و بازخوانی اگر منقضی شده باشد
    updateCachedResponse = async (
        cacheName: string
    ): Promise<AA_status<any>> => {
        const cachedApi = this.cacheStorage.load(cacheName);
        if (!cachedApi) { return { success: false, response: {} }; }
        // حذف cache قدیمی و درخواست مجدد API
        this.cacheStorage.remove(cacheName);
        const status = await this.dedupedCall(cacheName, cachedApi.api);
        if (status.success) { this.setCache(cacheName, { ...cachedApi, response: status.response, createdAt: Date.now() }); }
        return status;
    };
    // حذف cache
    removeCachedResponse = (cacheName: string) => this.cacheStorage.remove(cacheName);
    // بررسی اینکه dependency تغییر کرده است یا نه
    isDependencyChanged = (api: AA_api<any>): boolean => {
        if (!api.cache) return false;
        const cachedApi = this.cacheStorage.load(api.cache.name);
        if (!cachedApi) return false;
        return !IsEqual(cachedApi.dependency, api.cache.dependency);
    };
    // dump کامل cache (delegate به Storage)
    dump() { return this.cacheStorage.dump(); }
}
class OfflineClass {
    s: Storage
    request: any
    constructor(p: { id: string, request: any }) {
        this.s = new Storage(`${p.id}_offlines`)
        this.request = p.request
    }
    add = (api: AA_api<any>) => !api.offlineId ? undefined : this.s.save(api.offlineId, api)
    remove = (api: AA_api<any>) => !api.offlineId ? undefined : this.s.remove(api.offlineId)
    getList = (): AA_api<any>[] => this.s.getList()
    syncUnit = async (api: AA_api<any>): Promise<AA_status<any>> => {
        const status = await this.request(api);
        if (status.success) { this.remove(api); }
        return status
    }
    sync = async (): Promise<{ status: AA_status<any>, offlineId: string }[]> => {
        const list = this.getList();
        const report: { status: AA_status<any>, offlineId: string }[] = [];
        for (let i = 0; i < list.length; i++) {
            const api = list[i];
            if (!api.offlineId) { continue }
            const status = await this.syncUnit(api);
            report.push({ offlineId: api.offlineId, status })
        }
        return report
    }
}
function getErrorAlertConfig(api: AA_api<any>, status: AA_status<any>, logout: () => void, fa: boolean): AP_alert {
    let title: string = fa ? `${api.description || api.name} با خطا روبرو شد` : `An error was occured in ${api.description || api.name}`;
    const alertProps: AP_alert & { actions: any } = { type: 'error', title, text: status.errorMessage || '', time: api.messageTime, actions: [] };
    if (api.required) { alertProps.time = 10000000000; alertProps.onClose = false }
    if (api.reload !== false) { alertProps.actions.push({ text: fa ? 'بارگزاری مجدد' : 'Reload', id: 'Reload', callback: () => window.location.reload() }) }
    if (api.logout) { alertProps.actions.push({ text: fa ? 'خروج از حساب کاربری' : 'Logout', id: 'Logout', callback: () => { logout() } }) }
    if (api.retryAction) { alertProps.actions.push({ text: fa ? 'تلاش مجدد' : 'Retry', id: 'Retry', callback: () => { (api.retryAction || (() => { }))() }, close: true }) }
    return alertProps
}